import { SoldAs } from '@b-stock/order-api-client'
import { PricingStrategyEnum } from '@b-stock/search-api-client'

import getIsWithinBuyNowWindow from '@/buyer-portal/helpers/auctions/getIsWithinBuyNowWindow'

/**
 * Whether the buyer is bidding on an outcome or buying outright.
 *
 * Lives here rather than with the bid modal's copy: the tag, the shipping card
 * and the ALP all read it, and none of them should depend on a modal.
 */
export type SaleKind = 'bid' | 'purchase'

/** What `getIsWithinBuyNowWindow` reads, and nothing more. */
type BuyNowWindow = {
  buyNow?: { enabled?: boolean; endTime?: string }
  closed?: boolean
  canceled?: boolean
}

/**
 * Whether the buyer is bidding for this lot or buying it outright.
 *
 * Every Seller Collects surface that says what happens next has to agree about
 * this, or the banner, the shipping card and the tag describe one listing three
 * ways. Two shapes are easy to misread: an offering carries `auction: null`, so
 * presence alone reads as an auction; a Buy Now listing *is* an auction, with
 * `buyNow.enabled`, so reading the key alone leaves the win wording on the one
 * sale type it is wrong for.
 */
export const getSaleKind = (
  auction: BuyNowWindow | null | undefined
): SaleKind =>
  auction && !getIsWithinBuyNowWindow(auction) ? 'bid' : 'purchase'

/**
 * Same question of a listing-details object.
 *
 * Takes the loosest shape rather than the auction/offering union, because the
 * shipping hook is shared with callers typed to the common base — which has no
 * `auction` key, though the object it is handed at runtime does.
 */
export const getListingSaleKind = (
  listing: object | null | undefined
): SaleKind =>
  getSaleKind(
    listing && 'auction' in listing
      ? (listing as { auction?: BuyNowWindow | null }).auction
      : null
  )

/**
 * Same question of an already-placed order, which the live-window logic
 * above cannot answer: `getIsWithinBuyNowWindow` reads whether Buy Now is
 * open right now, and a completed order's auction is closed and its Buy Now
 * window long past by the time anyone looks — `getSaleKind` would read every
 * finished order as a bid, Buy Now included. `soldAs` is the order service's
 * own record of how the listing actually sold, so a finished order reads
 * that directly instead of reconstructing a live window state that no longer
 * applies.
 */
export const getOrderSaleKind = (
  listing: { soldAs?: SoldAs | null } | null | undefined
): SaleKind => (listing?.soldAs === SoldAs.BuyNow ? 'purchase' : 'bid')

/**
 * Same question on the listing page, for a buyer who already has an order
 * for it: `getListingSaleKind` only knows the live Buy Now window, so once
 * the buyer's own purchase closes that window — same listing, same buyer —
 * it reads their completed purchase as a loss, the gap `getOrderSaleKind`
 * exists to close on the order page. An order on hand is buyer-specific and
 * final, so it takes priority over the live window — but only when it gives
 * a definite answer: `soldAs` is only ever `Auction` or `BuyNow`, so an
 * offering order (no Buy Now window to begin with) has no `soldAs` and would
 * otherwise fall through `getOrderSaleKind` to 'bid', moving this same class
 * of bug onto offerings.
 */
export const getListingSaleKindForBuyer = (
  listing: object | null | undefined,
  order: { listing?: { soldAs?: SoldAs | null } | null } | null | undefined
): SaleKind =>
  order?.listing?.soldAs
    ? getOrderSaleKind(order.listing)
    : getListingSaleKind(listing)

/**
 * Same question of a search result, which carries the Buy Now window flat
 * where the listing page nests it. `MAKE_AN_OFFER` has no auction at all.
 *
 * Deliberately structural rather than typed to one result shape: the ALP hands
 * over a raw `SearchResult` with an ISO string, the grid a
 * `CombinedListingSearchResult` facade with a `Date`, and the two enums they
 * carry are different types spelling the same literals.
 */
export const getSearchResultSaleKind = (listing: {
  pricingStrategy?: string
  buyNow?: boolean
  buyNowEndTime?: string | Date | null
  // Absent on a raw search result, which only indexes live listings; the grid's
  // facade does expose them, and a closed or canceled lot is bid for, not
  // bought.
  closed?: boolean
  canceled?: boolean
}): SaleKind =>
  getSaleKind(
    listing.pricingStrategy === PricingStrategyEnum.Auction
      ? {
          closed: listing.closed,
          canceled: listing.canceled,
          buyNow: {
            enabled: listing.buyNow,
            endTime:
              listing.buyNowEndTime instanceof Date
                ? listing.buyNowEndTime.toISOString()
                : (listing.buyNowEndTime ?? undefined),
          },
        }
      : null
  )
